import { NavLink, Outlet } from "react-router-dom";

import { useUiStore } from "../../estado/ui-store";
import { flagAtiva } from "../../lib/flags/flags";
import { useTemPermissao } from "../../lib/auth/permissoes";

/*
 * Layout Shell do painel (FE-0 CP-D — ADR 0026).
 *
 * Estrutura: header + nav lateral + main. Usa SÓ classes Tailwind que apontam
 * para os tokens CPMH (cores da marca, fontes, espaçamento) — nenhum hex/px
 * literal aqui. O estado da nav lateral vem do ui-store (`navAberta`/
 * `alternarNav`) — client-state SÓ em Zustand (regra dura do ADR).
 *
 * Os itens de nav de MÓDULO são condicionados às flags dormentes (DEFAULT-OFF):
 * "Projeção" só aparece com `projecao_ativa`, "Conciliação" só com
 * `conciliacao_ativa`, "Cadastros" e "Trilha" só com `api_autenticada_disponivel`
 * (espelham o gate real do backend da API autenticada — auditoria não tem flag
 * própria; usa o mesmo gate dos CRUDs auditados, não há flag nova). "Alertas"
 * (histórico) só aparece com `api_autenticada_disponivel` E quando o usuário tem
 * a permissão `configurar_alertas` (mesma ação que governa o backend F5-BE3).
 */

interface ItemNav {
  rotulo: string;
  para: string;
  visivel: boolean;
}

/**
 * Monta os itens de nav. `podeConfigurarAlertas` e `podeDesfazer` são computados
 * pelo componente (via hook, no corpo) e injetados — não se chama hook dentro de
 * `.map`/`.filter`.
 */
function itensNav(
  podeConfigurarAlertas: boolean,
  podeDesfazer: boolean,
  podeLancarReceita: boolean,
): ItemNav[] {
  return [
    { rotulo: "Dashboard", para: "/", visivel: true },
    {
      rotulo: "Projeção",
      para: "/projecao",
      visivel: flagAtiva("projecao_ativa"),
    },
    {
      rotulo: "Conciliação",
      para: "/conciliacao",
      visivel: flagAtiva("conciliacao_ativa"),
    },
    {
      rotulo: "Desfazer conciliação",
      para: "/conciliacao/desfazer",
      // Tela 4 (FE-4): gate da API autenticada E permissão `desfazer_conciliacao`.
      visivel: flagAtiva("api_autenticada_disponivel") && podeDesfazer,
    },
    {
      rotulo: "Cadastros",
      para: "/cadastros",
      // Espelha o gate real do backend (CRUDs sob `api_autenticada_disponivel`).
      visivel: flagAtiva("api_autenticada_disponivel"),
    },
    {
      rotulo: "Trilha",
      para: "/trilha",
      // Auditoria não tem flag própria — mesmo gate da API autenticada.
      visivel: flagAtiva("api_autenticada_disponivel"),
    },
    {
      rotulo: "Alertas",
      para: "/historico/alertas",
      // Gate da API autenticada E permissão `configurar_alertas` (F5-BE3).
      visivel:
        flagAtiva("api_autenticada_disponivel") && podeConfigurarAlertas,
    },
    {
      rotulo: "Regras e Alertas",
      para: "/regras",
      // Tela 6 (Regras e Canais, FE-6a): mesmo gate do item "Alertas" — flag da
      // API autenticada E permissão `configurar_alertas` (espelha o backend #153).
      visivel:
        flagAtiva("api_autenticada_disponivel") && podeConfigurarAlertas,
    },
    {
      rotulo: "Receita manual",
      para: "/receitas/manual",
      // Tela 5 (FE — receita manual): só com a permissão `criar_receita_manual`
      // (a tela é EMPRESA-only; o backend isola por escopo no path).
      visivel: podeLancarReceita,
    },
  ];
}

export function Shell(): JSX.Element {
  const navAberta = useUiStore((estado) => estado.navAberta);
  const alternarNav = useUiStore((estado) => estado.alternarNav);
  const podeConfigurarAlertas = useTemPermissao("configurar_alertas");
  const podeDesfazer = useTemPermissao("desfazer_conciliacao");
  const podeLancarReceita = useTemPermissao("criar_receita_manual");

  return (
    <div className="min-h-screen bg-snow font-corpo text-off-black">
      <header className="flex items-center gap-4 bg-off-black px-4 py-3 text-snow">
        <button
          type="button"
          onClick={alternarNav}
          aria-label="Alternar navegação"
          aria-expanded={navAberta}
          className="rounded-md px-2 py-1 text-snow hover:text-flame"
        >
          ☰
        </button>
        <span className="font-titulo text-lg text-snow">
          Fluxo de Caixa Consolidado
        </span>
      </header>

      <div className="flex">
        {navAberta && (
          <nav
            aria-label="Navegação principal"
            className="flex w-48 flex-col gap-2 bg-platinum p-4"
          >
            {itensNav(podeConfigurarAlertas, podeDesfazer, podeLancarReceita)
              .filter((item) => item.visivel)
              .map((item) => (
                <NavLink
                  key={item.para}
                  to={item.para}
                  end={item.para === "/"}
                  className="font-subtitulo text-base text-off-black hover:text-flame"
                >
                  {item.rotulo}
                </NavLink>
              ))}
          </nav>
        )}

        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
